import React, { useState, useRef } from 'react';
import FormSelectBox from '../../../../../components/Form/FormSelectBox';
import { Button } from 'react-bootstrap';
import { useHabilitacionContratista } from '../../../../../hooks/useHabilitacionContratista';
import { useDocumentoHabilitacion } from '../../../../../hooks/useDocumentoHabilitacion';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import MyModal from '../../../../../components/Modal';

type AgregarDocumentoHabilitacionProps = {
    tiposDocumento: any[];
    cancelButtonFn?: () => void;
    saveCompletedButtonFn?: () => void;
};

export const AgregarDocumentoHabilitacion: React.FC<AgregarDocumentoHabilitacionProps> = ({
    tiposDocumento,
    cancelButtonFn,
    saveCompletedButtonFn,
}) => {
    const { habilitacionSelected, buscarHabilitacion } = useHabilitacionContratista();
    const { buscarDocumentosByIdHabilitacion, handleLoading, subirDocumento } = useDocumentoHabilitacion();

    const [idTipoDocumento, setIdTipoDocumento] = useState<number | undefined>();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isFechaEditable, setIsFechaEditable] = useState(false);
    const [fechaVencimiento, setFechaVencimiento] = useState<string>('');
    const [notSaveDocumentModal, setNotSaveDocumentModal] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const btnRef = useRef<HTMLButtonElement>(null);

    const toggleNotSaveDocument = () =>
        setNotSaveDocumentModal(!notSaveDocumentModal);

    const handleFileChange = () => {
        if (fileInputRef.current?.files && fileInputRef.current.files[0]) {
            setSelectedFile(fileInputRef.current.files[0]);
        }
    };

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleSaveButtonClick = () => {
        if (!idTipoDocumento) {
            toast.error('Por favor, seleccione un Tipo de Documento.');
            return;
        }
        if (!selectedFile) {
            toast.error('Por favor, cargue un documento.');
            return;
        }
        if (isFechaEditable && !fechaVencimiento) {
            toast.error('Por favor, seleccione una fecha de vencimiento.');
            return;
        }
        if (isFechaEditable && fechaVencimiento) {
            const fechaSeleccionada = new Date(fechaVencimiento);
            const fechaActual = new Date();
            fechaActual.setHours(0, 0, 0, 0);
            if (fechaSeleccionada <= fechaActual) {
                toast.error('La fecha de vencimiento debe ser mayor a la fecha actual.');
                return;
            }
        }

        if (btnRef.current) btnRef.current.disabled = true;

        const uniqueId: string = uuidv4();
        subirDocumento(
            selectedFile,
            {
                idTipoDocumento: idTipoDocumento,
                idHabilitacionContratista: habilitacionSelected.id,
                fileId: uniqueId,
                sizeMaximo: 20,
                fechaVencimiento: isFechaEditable ? fechaVencimiento : null,
            }
        )
            .then((result) => {
                if (result) {
                    handleLoading(true);
                    buscarDocumentosByIdHabilitacion(habilitacionSelected.id);
                    // Refresca el estado de la habilitación: si estaba "Aprobada", el backend
                    // la pasó a "Actualizacion" y debe re-evaluarse la botonera (mostrar "Enviar").
                    buscarHabilitacion(String(habilitacionSelected.id));
                    saveCompletedButtonFn?.();
                } else {
                    if (btnRef.current) btnRef.current.disabled = false;
                }
            })
            .catch(() => {
                if (btnRef.current) btnRef.current.disabled = false;
            });
    };

    const handleExitButtonClick = () => {
        if (selectedFile) {
            setNotSaveDocumentModal(true);
            return;
        }
        cancelButtonFn?.();
    };

    return (
        <>
            <MyModal
                isOpen={notSaveDocumentModal}
                toggle={toggleNotSaveDocument}
                title="Advertencia"
                cancelButton={true}
                secondButton={true}
                secondButtonTitle={'Aceptar'}
                secondButtonFn={() => {
                    toggleNotSaveDocument();
                    cancelButtonFn?.();
                }}
            >
                <label>Desea Salir sin guardar los cambios?</label>
            </MyModal>
            <div className="form-horizontal">
                <div className="row mt-2">
                    <FormSelectBox
                        title={'Tipo de Documento'}
                        label={'tipo de documento'}
                        id={'tipoDocumentoHab'}
                        name={'idTipoDocumento'}
                        optionId={'idTipoDocumento'}
                        optionLabel={'nombre'}
                        value={idTipoDocumento}
                        options={tiposDocumento}
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                            const parsedValue = parseInt(event.target.value, 10);
                            setIdTipoDocumento(parsedValue);
                            const tipoDoc = tiposDocumento.find(
                                (t) => t.idTipoDocumento === parsedValue
                            );
                            setIsFechaEditable(tipoDoc?.fechaEditable || false);
                            if (!tipoDoc?.fechaEditable) {
                                setFechaVencimiento('');
                            }
                        }}
                        classLabel={'col-form-label col-4 text-end'}
                        classDivSelect="col-8"
                    />
                </div>
                <div className="row mt-2">
                    <div className="col-md-4 text-end">
                        <label className="col-form-label">Documento</label>
                        <input
                            type="file"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                        />
                    </div>
                    <div className="col-md-8">
                        <div className="d-flex align-items-center justify-content-between">
                            <Button
                                className="btn-secondary pr-2"
                                variant="secondary"
                                onClick={handleButtonClick}
                            >
                                Examinar
                            </Button>
                            <div>
                                <input
                                    type="text"
                                    style={{ width: '200px', height: '35px' }}
                                    value={
                                        selectedFile
                                            ? selectedFile.name
                                            : 'Ningún archivo seleccionado'
                                    }
                                    readOnly
                                />
                            </div>
                        </div>
                    </div>
                    {isFechaEditable && (
                        <div className="row mt-2">
                            <div className="col-md-4 text-end">
                                <label className="col-form-label">
                                    Fecha Vencimiento{' '}
                                    <span className="text-danger">*</span>
                                </label>
                            </div>
                            <div className="col-md-8">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={fechaVencimiento}
                                    min={new Date().toISOString().split('T')[0]}
                                    onChange={(e) =>
                                        setFechaVencimiento(e.target.value)
                                    }
                                    style={{ width: '200px', height: '35px' }}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="row mt-2">
                    <div className="col-md-9"></div>
                    <div className="col-md-3 d-flex justify-content-end">
                        <Button
                            color="secondary"
                            onClick={handleSaveButtonClick}
                            className="mr-2"
                            ref={btnRef}
                        >
                            Guardar
                        </Button>
                        <Button
                            color="secondary"
                            onClick={handleExitButtonClick}
                            className="mr-2"
                        >
                            Salir
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};
